import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { DEFINITIVE_IDENTIFIER_PATTERN } from '@/clinical-study/vocabulary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { usePendingWrite } from '@/lib/use-pending-write';

type StudySubject =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/treatment-execution-confirmation']['post']['responses'][200]['content']['application/json'];

const confirmationSchema = z.object({
  actualTreatmentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La data deve essere nel formato AAAA-MM-GG.'),
  definitiveIdentifier: z
    .string()
    .regex(DEFINITIVE_IDENTIFIER_PATTERN, 'Deve contenere solo cifre, da 3 a 8 caratteri.'),
});
type ConfirmationForm = z.infer<typeof confirmationSchema>;

/**
 * `TreatmentExecutionConfirmationSection` (FR-9.28, FR-9.4): the single treatment-execution
 * confirmation action — actual Treatment Date + definitive `NNN` identifier — that enrolls the
 * Study Subject in one transition. Reuses the already-mirrored `DEFINITIVE_IDENTIFIER_PATTERN`
 * for client-side format validation, mirroring `IdentifierCorrectionSection.tsx`'s
 * `correctedIdentifier` field exactly (same pattern, same hint convention). Standard inline
 * write-error surfacing (Resolution R3) — not a persistent banner, since this is not a
 * capacity-adjacent write.
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 3.3.1/4.1.3, Finding 4): `apiError`/`confirmed`
 * and both field-validation messages now render through `FormFeedback`; `treatment-execution-
 * actual-date`/`-definitive-identifier` carry `aria-invalid`/`aria-describedby` (the latter
 * combining the existing hint id with the new error id when invalid).
 */
export function TreatmentExecutionConfirmationSection({
  studyId,
  subjectId,
  onConfirmed,
}: {
  studyId: string;
  subjectId: string;
  onConfirmed: (subject: StudySubject) => void;
}) {
  const { t } = useTranslation('clinical-study');
  const [apiError, setApiError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfirmationForm>({ resolver: zodResolver(confirmationSchema) });
  const { pending, run } = usePendingWrite();

  const onSubmit = async (values: ConfirmationForm) => {
    setApiError(null);
    setConfirmed(false);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/treatment-execution-confirmation',
      {
        params: { path: { studyId, subjectId } },
        body: {
          actualTreatmentDate: values.actualTreatmentDate,
          definitiveIdentifier: values.definitiveIdentifier,
        },
      },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('treatmentExecution.genericError'));
      return;
    }
    reset();
    setConfirmed(true);
    onConfirmed(data);
    toast.success(t('treatmentExecution.confirmedToast'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('treatmentExecution.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
        >
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="treatment-execution-actual-date">
              {t('treatmentExecution.actualTreatmentDateLabel')}
            </label>
            <Input
              id="treatment-execution-actual-date"
              type="date"
              aria-invalid={errors.actualTreatmentDate ? true : undefined}
              aria-describedby={
                errors.actualTreatmentDate ? 'treatment-execution-actual-date-error' : undefined
              }
              {...register('actualTreatmentDate')}
            />
            {errors.actualTreatmentDate && (
              <FormFeedback tone="error" id="treatment-execution-actual-date-error">
                {errors.actualTreatmentDate.message}
              </FormFeedback>
            )}
          </div>
          <div className="space-y-1">
            <label
              className="text-sm font-medium"
              htmlFor="treatment-execution-definitive-identifier"
            >
              {t('treatmentExecution.definitiveIdentifierLabel')}
            </label>
            <Input
              id="treatment-execution-definitive-identifier"
              aria-invalid={errors.definitiveIdentifier ? true : undefined}
              aria-describedby={
                errors.definitiveIdentifier
                  ? 'treatment-execution-definitive-identifier-hint treatment-execution-definitive-identifier-error'
                  : 'treatment-execution-definitive-identifier-hint'
              }
              {...register('definitiveIdentifier')}
            />
            <p
              id="treatment-execution-definitive-identifier-hint"
              className="text-xs text-muted-foreground"
            >
              {t('treatmentExecution.definitiveIdentifierHint')}
            </p>
            {errors.definitiveIdentifier && (
              <FormFeedback tone="error" id="treatment-execution-definitive-identifier-error">
                {errors.definitiveIdentifier.message}
              </FormFeedback>
            )}
          </div>
          <Button type="submit" disabled={pending}>
            {t('treatmentExecution.submit')}
          </Button>
          {apiError && <FormFeedback tone="error">{apiError}</FormFeedback>}
          {confirmed && (
            <FormFeedback tone="success">{t('treatmentExecution.confirmed')}</FormFeedback>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
